import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Label, Input, PrimaryBtn, GhostBtn, Modal } from "@/components/admin/ui";
import { Plus, Pencil, Trash2, Folder, Upload, ChevronLeft, Loader2, Film, Image as ImageIcon, Star } from "lucide-react";

export default function GalleryPanel() {
  const [folders, setFolders] = useState([]);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/gallery/folders").then((r) => setFolders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const removeFolder = async (id) => {
    if (!window.confirm("Eliminare l'album e tutti i suoi media?")) return;
    await api.delete(`/gallery/folders/${id}`);
    toast.success("Album eliminato");
    load();
  };

  if (selected) {
    return <FolderView folder={selected} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl font-bold uppercase text-zinc-100">Album galleria</h2>
        <PrimaryBtn onClick={() => setCreating(true)} data-testid="new-folder-btn">
          <Plus className="h-4 w-4" /> Nuovo album
        </PrimaryBtn>
      </div>

      {folders.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center">Nessun album. Creane uno per caricare foto e video.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {folders.map((f) => (
            <div key={f.id} data-testid={`admin-folder-${f.id}`} className="border border-zinc-800 bg-zinc-900 p-5">
              <Folder className="h-6 w-6 text-yellow-400 mb-2" />
              <h3 className="font-heading text-lg font-bold text-zinc-50 truncate">{f.name}</h3>
              <p className="text-xs text-zinc-500">{f.count} element{f.count === 1 ? "o" : "i"}</p>
              <div className="mt-4 flex gap-2">
                <GhostBtn onClick={() => setSelected(f)} data-testid={`open-folder-${f.id}`} className="flex-1">
                  <Upload className="h-3.5 w-3.5" /> Apri
                </GhostBtn>
                <button onClick={() => setRenaming(f)} data-testid={`rename-folder-${f.id}`}
                  className="border border-zinc-700 p-2.5 text-zinc-300 hover:text-yellow-400 hover:border-yellow-400 transition-colors duration-200">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => removeFolder(f.id)} data-testid={`delete-folder-${f.id}`}
                  className="border border-zinc-700 p-2.5 text-zinc-300 hover:text-red-400 hover:border-red-400 transition-colors duration-200">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <FolderForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {renaming && <FolderForm folder={renaming} onClose={() => setRenaming(null)} onSaved={() => { setRenaming(null); load(); }} />}
    </div>
  );
}

function FolderForm({ folder, onClose, onSaved }) {
  const [name, setName] = useState(folder?.name || "");
  const [saving, setSaving] = useState(false);
  const isEdit = !!folder;

  const save = async () => {
    if (!name.trim()) { toast.error("Inserisci un titolo"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/gallery/folders/${folder.id}`, { name });
      else await api.post("/gallery/folders", { name });
      toast.success(isEdit ? "Album rinominato" : "Album creato");
      onSaved();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? "Rinomina album" : "Nuovo album"} onClose={onClose} testid="folder-form">
      <div className="space-y-4">
        <div>
          <Label>Titolo album</Label>
          <Input data-testid="folder-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2" placeholder="es. Gran Premio 2026" />
        </div>
        <div className="flex justify-end gap-3">
          <GhostBtn onClick={onClose}>Annulla</GhostBtn>
          <PrimaryBtn onClick={save} disabled={saving} data-testid="save-folder-btn">
            {saving ? "Salvataggio…" : "Salva"}
          </PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

function FolderView({ folder, onBack }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const load = () => api.get(`/gallery?folder_id=${folder.id}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    const hasProcessing = items.some((it) => it.status === "processing");
    if (!hasProcessing) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [items]); // eslint-disable-line

  const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per blocco (sotto il limite del proxy)

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const { data: initData } = await api.post("/gallery/upload/init");
      const uploadId = initData.upload_id;
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("file", blob, file.name);
        await api.post("/gallery/upload/chunk", fd);
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      const done = new FormData();
      done.append("upload_id", uploadId);
      done.append("folder_id", folder.id);
      done.append("filename", file.name);
      done.append("title", title);
      done.append("content_type", file.type || "");
      await api.post("/gallery/upload/complete", done);
      toast.success("Caricato");
      setTitle("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo media?")) return;
    await api.delete(`/gallery/${id}`);
    toast.success("Eliminato");
    load();
  };

  const setCover = async (id) => {
    await api.post(`/gallery/folders/${folder.id}/cover`, { cover_id: id });
    toast.success("Copertina album impostata");
  };

  return (
    <div>
      <button onClick={onBack} data-testid="back-to-folders-admin"
        className="mb-4 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 hover:text-yellow-400 transition-colors duration-200">
        <ChevronLeft className="h-4 w-4" /> Album
      </button>
      <h2 className="font-heading text-xl font-bold uppercase text-zinc-100 mb-1">{folder.name}</h2>
      <p className="text-xs text-zinc-500 mb-6">Carica foto o video in qualsiasi formato.</p>

      <div className="border border-zinc-800 bg-zinc-900 p-5 mb-8 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <Label>Titolo (opzionale)</Label>
          <Input data-testid="upload-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2" />
        </div>
        <input ref={fileRef} type="file" accept="image/*,video/*,.heic,.heif,.avif,.mkv,.avi,.mov,.wmv,.flv,.m4v,.3gp,.3g2,.ts,.mts,.m2ts,.ogv,.webm,.mpg,.mpeg,.tif,.tiff,.bmp,.svg,.raw,.dng,.cr2,.nef,.arw" onChange={onFile} className="hidden" data-testid="gallery-file" />
        <PrimaryBtn onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-media-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? `Caricamento… ${progress}%` : "Carica media"}
        </PrimaryBtn>
      </div>

      {uploading && (
        <div className="mb-8 -mt-4" data-testid="upload-progress">
          <div className="h-2 w-full bg-zinc-800 overflow-hidden rounded-full">
            <div className="h-full bg-yellow-400 transition-all duration-200" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-1.5 text-[10px] uppercase tracking-wider text-zinc-500">Caricamento in corso: {progress}% — non chiudere la pagina</p>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center">Album vuoto.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`admin-media-${it.id}`} className="relative aspect-square border border-zinc-800 bg-zinc-950 overflow-hidden group">
              {it.status === "processing" ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-center px-2" data-testid={`media-processing-${it.id}`}>
                  <Loader2 className="h-6 w-6 text-yellow-400 animate-spin" />
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400">Compressione video…</span>
                </div>
              ) : it.status === "failed" ? (
                <div className="w-full h-full flex items-center justify-center text-center px-2">
                  <span className="text-[10px] uppercase tracking-wider text-red-400">Elaborazione fallita</span>
                </div>
              ) : it.media_type === "video" ? (
                <video src={`${API}/gallery/file/${it.id}`} muted className="w-full h-full object-cover opacity-80" />
              ) : (
                <img src={`${API}/gallery/file/${it.id}`} alt={it.title} className="w-full h-full object-cover" />
              )}
              <span className="absolute top-2 left-2 text-cyan-400 bg-zinc-950/80 rounded-full p-1">
                {it.media_type === "video" ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
              </span>
              <div className="absolute top-2 right-2 flex gap-1">
                <button onClick={() => setCover(it.id)} data-testid={`cover-media-${it.id}`} title="Imposta come copertina"
                  className="bg-zinc-950/80 text-zinc-300 hover:text-yellow-400 p-1.5 transition-colors duration-200">
                  <Star className="h-4 w-4" />
                </button>
                <button onClick={() => remove(it.id)} data-testid={`delete-media-${it.id}`}
                  className="bg-zinc-950/80 text-zinc-300 hover:text-red-400 p-1.5 transition-colors duration-200">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
